import { useDisclosure } from '@mantine/hooks';
import { usePricingQuery } from '@xver/hooks/usePricingQuery';
import { useQuotasQuery } from '@xver/hooks/useQuotasQuery';
import { maxLaunchQuantity } from '@xver/utils/quota';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CloudPackageCategory } from '../../../../../apis/v1/instances/model';
import { useAddSSHKeyForm } from './useAddSSHKeyForm';
import { useCloudPackagesQuery } from './useCloudPackagesQuery';
import { useLaunchInstanceMutate } from './useLaunchInstanceMutate';
import { useLaunchOptionQuery } from './useLaunchOptionQuery';
import { useSSHKeyQuery } from './useSSHKeyQuery';

export type { InstanceOption } from '../../../../../components/Organisms/InstanceFamilyOptionList/InstanceFamilyOptionList';

export type InstanceFamily = 'shared' | 'general-purpose' | 'dedicated';

const FAMILY_TO_CATEGORY: Record<InstanceFamily, CloudPackageCategory> = {
    shared: 'shared',
    'general-purpose': 'general',
    dedicated: 'dedicated',
};

export interface LaunchOS {
    name: string;
    slug: string;
    versions: Array<{
        name: string;
        slug: string;
    }>;
}

export function useCloudComputeDeploymentPage() {
    const { t } = useTranslation('validation');
    const [addSSHKeyModalOpened, { open: openAddSSHKeyModal, close: closeAddSSHKeyModal }] =
        useDisclosure(false);

    const {
        register: registerSSHKey,
        errors: sshKeyErrors,
        isSubmitting: isAddSSHKeySubmitting,
        reset: resetSSHKeyForm,
        onSubmit: handleAddSSHKey,
    } = useAddSSHKeyForm({ onSuccess: closeAddSSHKeyModal });

    const handleCloseAddSSHKeyModal = () => {
        resetSSHKeyForm();
        closeAddSSHKeyModal();
    };

    const { data: launchOptions, isPending: isLaunchOptionsPending } = useLaunchOptionQuery();
    const { data: cloudPackagesResp, isPending: isCloudPackagesPending } = useCloudPackagesQuery();
    const { data: sshKeys = [], isPending: isSSHKeysLoading } = useSSHKeyQuery();
    const { data: pricingResp } = usePricingQuery();
    const backupGbHourlyRate = pricingResp?.products.backup.gb_hourly_rate ?? 0.0025;
    const diskAdditionGbMonthlyRate =
        pricingResp?.products.instance.disk_addition_gb_monthly_rate ?? 5;
    const launchInstanceMutation = useLaunchInstanceMutate();
    const { instances: instanceQuota } = useQuotasQuery();
    const maxQuantity = maxLaunchQuantity(instanceQuota);

    const [selectedOs, setSelectedOs] = useState<LaunchOS | null>(null);
    const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
    const [osType, setOsType] = useState<'linux' | 'windows'>('linux');
    const [selectedInstanceFamily, setSelectedInstanceFamily] = useState<InstanceFamily>('shared');
    const [selectedInstanceSku, setSelectedInstanceSku] = useState<string | null>(null);
    const [selectedSshKeys, setSelectedSshKeys] = useState<string[]>([]);
    const [startupScript, setStartupScript] = useState<string>('');
    const [hostname, setHostname] = useState('');
    const [isBackupEnabled, setIsBackupEnabled] = useState<boolean>(false);
    const [isDiskAdditionEnabled, setIsDiskAdditionEnabled] = useState<boolean>(false);
    const [diskAdditionGb, setDiskAdditionGb] = useState<number>(1);
    const [tags, setTags] = useState<string[]>([]);
    const [quantity, setQuantity] = useState<number>(1);

    useEffect(() => {
        setHostname(
            (currentHostname) => currentHostname || `xver-${Math.random().toString(36).slice(2, 8)}`
        );
    }, []);

    const filteredOsOptions = useMemo(() => {
        const all = launchOptions?.os ?? [];
        return all.filter((os) => {
            const isWindows = os.slug.toLowerCase().includes('windows');
            return osType === 'windows' ? isWindows : !isWindows;
        });
    }, [launchOptions, osType]);

    const instanceOptions = (cloudPackagesResp?.packages || [])
        .filter((pkg) => pkg.category === FAMILY_TO_CATEGORY[selectedInstanceFamily])
        .filter((pkg) => (osType === 'windows' ? pkg.cpu >= 1 && pkg.memory / 1024 > 1 : true))
        .map((pkg) => ({
            sku: pkg.slug,
            vcpu: pkg.cpu,
            memory: `${pkg.memory / 1024} GB`,
            ssd: `${pkg.disk} GB`,
            bandwidth: `${pkg.bandwidth} TB`,
            priceHr: `${pkg.price.hourly.toFixed(2)}`,
            priceMo: `${pkg.price.monthly.toFixed(2)}`,
        }));

    useEffect(() => {
        if (launchOptions?.os && launchOptions.os.length > 0) {
            const isWindows = (os: LaunchOS) => os.slug.toLowerCase().includes('windows');
            const filteredOs = launchOptions.os.filter((os) =>
                osType === 'windows' ? isWindows(os) : !isWindows(os)
            );
            if (filteredOs.length > 0) {
                setSelectedOs(filteredOs[0]);
                setSelectedVersion(filteredOs[0].versions?.[0]?.slug || null);
            }
        }
    }, [launchOptions, osType]);

    useEffect(() => {
        const isSelectedInstanceSkuValid = instanceOptions.some(
            (option) => option.sku === selectedInstanceSku
        );

        if ((!selectedInstanceSku || !isSelectedInstanceSkuValid) && instanceOptions.length > 0) {
            setSelectedInstanceSku(instanceOptions[0].sku);
        }
    }, [instanceOptions, selectedInstanceSku]);

    const selectedInstanceOption = selectedInstanceSku
        ? instanceOptions.find((option) => option.sku === selectedInstanceSku)
        : undefined;
    const selectedInstanceSizeGb = selectedInstanceOption
        ? Number((selectedInstanceOption.ssd || '').replace(/GB/i, '').trim()) || 0
        : 0;
    const unitHourlyPrice = selectedInstanceOption
        ? Number(selectedInstanceOption.priceHr) || 0
        : 0;
    const unitMonthlyPrice = selectedInstanceOption
        ? Number(selectedInstanceOption.priceMo) || 0
        : 0;
    const instanceHourlyTotalPrice = unitHourlyPrice * quantity;
    const computeHourlyTotalPrice = instanceHourlyTotalPrice;
    const effectiveDiskAdditionGb = isDiskAdditionEnabled ? diskAdditionGb : 0;
    const diskAdditionHourlyTotalPrice =
        (effectiveDiskAdditionGb * diskAdditionGbMonthlyRate * quantity) / 730;
    const diskAdditionMonthlyTotalPrice =
        effectiveDiskAdditionGb * diskAdditionGbMonthlyRate * quantity;
    const backupHourlyTotalPrice = isBackupEnabled
        ? (selectedInstanceSizeGb + effectiveDiskAdditionGb) * 0.6 * backupGbHourlyRate * quantity
        : 0;
    const hourlyTotalPrice =
        instanceHourlyTotalPrice + backupHourlyTotalPrice + diskAdditionHourlyTotalPrice;
    const monthlyTotalPrice =
        unitMonthlyPrice * quantity + backupHourlyTotalPrice * 730 + diskAdditionMonthlyTotalPrice;
    const backupEstimatedCost = selectedInstanceOption
        ? `${backupHourlyTotalPrice.toFixed(4)}`
        : '—';
    const diskAdditionEstimatedCost = selectedInstanceOption
        ? `${diskAdditionHourlyTotalPrice.toFixed(4)}`
        : '—';

    const hostnameError =
        hostname.length > 0 && hostname.length < 4 ? t('hostname.length') : undefined;

    const handleHostnameChange = (value: string) => {
        const sanitized = value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20);
        setHostname(sanitized);
    };

    const handleHostnamePaste = (event: any) => {
        event.preventDefault();
        const paste = (event.clipboardData?.getData && event.clipboardData.getData('text')) || '';
        const sanitized = paste.replace(/[^a-zA-Z0-9-]/g, '');
        const input = event.currentTarget as HTMLInputElement;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const remaining = 20 - (input.value.length - (end - start));
        const toInsert = sanitized.slice(0, Math.max(0, remaining));
        const newVal = input.value.slice(0, start) + toInsert + input.value.slice(end);
        setHostname(newVal);
    };

    const handleGenerateHostname = () => {
        setHostname(`xver-${Math.random().toString(36).slice(2, 8)}`);
    };

    const handleQuantityChange = (value: string | number) => {
        const nextQuantity = Number(value) || 1;
        setQuantity(Math.max(1, Math.min(maxQuantity, Math.floor(nextQuantity))));
    };

    const handleDeployInstance = () => {
        const selectedPackage = (cloudPackagesResp?.packages || []).find(
            (pkg) => pkg.slug === selectedInstanceSku
        );

        if (!selectedPackage || !selectedOs) return;

        launchInstanceMutation.mutate({
            os: selectedOs.slug,
            image: selectedVersion || '',
            cloudPkg: selectedPackage.id,
            hostname,
            keys: selectedSshKeys,
            enableStartupScript: startupScript.trim().length > 0,
            enableBackup: isBackupEnabled,
            startupScript,
            tags,
            quantity,
            diskAddition: effectiveDiskAdditionGb,
        });
    };

    return {
        addSSHKeyModalOpened,
        backupEstimatedCost,
        closeAddSSHKeyModal,
        computeHourlyTotalPrice,
        diskAdditionEstimatedCost,
        diskAdditionGb,
        diskAdditionGbMonthlyRate,
        filteredOsOptions,
        launchOptions,
        handleAddSSHKey,
        handleCloseAddSSHKeyModal,
        handleDeployInstance,
        handleGenerateHostname,
        handleHostnameChange,
        handleHostnamePaste,
        handleQuantityChange,
        hostname,
        hostnameError,
        instanceOptions,
        isAddSSHKeySubmitting,
        isBackupEnabled,
        isDiskAdditionEnabled,
        isLaunchOptionsPending: isLaunchOptionsPending || isCloudPackagesPending,
        isSSHKeyQueryLoading: isSSHKeysLoading,
        isDeployLoading: launchInstanceMutation.isPending,
        monthlyTotalPrice,
        openAddSSHKeyModal,
        osType,
        quantity,
        maxQuantity,
        registerSSHKey,
        resetSSHKeyForm,
        selectedInstanceFamily,
        selectedInstanceOption,
        selectedInstanceSku,
        selectedOs,
        selectedSshKeys,
        selectedVersion,
        setDiskAdditionGb,
        setHostname,
        setIsBackupEnabled,
        setIsDiskAdditionEnabled,
        setOsType,
        setSelectedInstanceFamily,
        setSelectedInstanceSku,
        setSelectedOs,
        setSelectedSshKeys,
        setSelectedVersion,
        setStartupScript,
        setQuantity,
        setTags,
        sshKeyErrors,
        sshKeys,
        startupScript,
        tags,
        totalHourlyPrice: hourlyTotalPrice,
    };
}
